/**
 * VideoDebugStats - Real-time WebRTC stats panel for debug/testing mode
 * Shows bitrate, resolution, codec, packet loss, jitter, RTT, simulcast layers,
 * quality limitations etc. Only rendered when system testing mode is active.
 */

import { useState, useEffect, useCallback } from "react";
import { Room, LocalTrackPublication, RemoteTrackPublication, Track } from "livekit-client";
import { Activity, MonitorUp, MonitorDown, Settings2 } from "lucide-react";

interface TrackStats {
  kind: "video" | "audio";
  direction: "send" | "recv";
  label: string;
  codec?: string;
  resolution?: string;
  framerate?: number;
  bitrate: number; // bps
  packetLoss: number; // percentage
  jitter?: number; // ms
  /** Simulcast layer id (q=quarter, h=half, f=full) */
  rid?: string;
  /** Why quality is limited: "none" | "cpu" | "bandwidth" | "other" */
  qualityLimitationReason?: string;
  /** Target bitrate set by the encoder */
  targetBitrate?: number;
  /** Whether this layer is actively sending */
  active: boolean;
}

interface CaptureInfo {
  /** Actual resolution the camera hardware is producing */
  resolution?: string;
  /** Requested resolution from getUserMedia constraints */
  requested?: string;
}

interface RoomStats {
  tracks: TrackStats[];
  rtt?: number;
  /** Total send bitrate across all tracks */
  totalSendBitrate: number;
  /** Total receive bitrate across all tracks */
  totalRecvBitrate: number;
  /** Actual camera capture info (from MediaStreamTrack.getSettings) */
  capture?: CaptureInfo;
  timestamp: number;
}

// Previous snapshot for delta calculations
let prevReport: Map<string, { bytes: number; packets: number; packetsLost: number; timestamp: number }> = new Map();

function formatBitrate(bps: number): string {
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`;
  if (bps >= 1_000) return `${(bps / 1_000).toFixed(0)} kbps`;
  return `${bps.toFixed(0)} bps`;
}

const ridLabels: Record<string, string> = {
  q: "Low",
  h: "Mid",
  f: "High",
};

const limitationLabels: Record<string, string> = {
  none: "None",
  cpu: "CPU",
  bandwidth: "Bandwidth",
  other: "Other",
};

async function collectStats(room: Room): Promise<RoomStats> {
  const tracks: TrackStats[] = [];
  const now = Date.now();

  // Collect local (send) track stats
  for (const pub of room.localParticipant.trackPublications.values()) {
    const lp = pub as LocalTrackPublication;
    if (!lp.track?.sender) continue;

    try {
      const report = await lp.track.sender.getStats();

      // Build codec map first
      const codecs = new Map<string, string>();
      for (const stat of report.values()) {
        if (stat.type === "codec" && stat.mimeType) {
          codecs.set(stat.id, stat.mimeType.split("/")[1]?.toUpperCase() ?? "");
        }
      }

      for (const stat of report.values()) {
        if (stat.type === "outbound-rtp" && (stat.kind === "video" || stat.kind === "audio")) {
          const prevSnap = prevReport.get(stat.id);
          const dt = prevSnap ? (now - prevSnap.timestamp) / 1000 : 1;
          const bytesDelta = prevSnap ? stat.bytesSent - prevSnap.bytes : 0;
          const bitrate = dt > 0 ? (bytesDelta * 8) / dt : 0;
          const packetsDelta = prevSnap ? stat.packetsSent - prevSnap.packets : 0;

          // Determine if this layer is actually sending data
          const active = prevSnap ? bytesDelta > 0 : true;

          const baseLabel = lp.source === Track.Source.ScreenShare
            ? "Screen"
            : stat.kind === "video" ? "Camera" : "Mic";

          const info: TrackStats = {
            kind: stat.kind,
            direction: "send",
            label: baseLabel,
            bitrate,
            packetLoss: 0,
            rid: stat.rid || undefined,
            qualityLimitationReason: stat.qualityLimitationReason || undefined,
            targetBitrate: stat.targetBitrate || undefined,
            active,
          };

          if (stat.kind === "video") {
            info.resolution = stat.frameWidth && stat.frameHeight
              ? `${stat.frameWidth}x${stat.frameHeight}`
              : undefined;
            info.framerate = stat.framesPerSecond ?? undefined;
          }

          if (stat.codecId) {
            info.codec = codecs.get(stat.codecId);
          }

          prevReport.set(stat.id, {
            bytes: stat.bytesSent,
            packets: stat.packetsSent,
            packetsLost: 0,
            timestamp: now,
          });

          // Show if we have delta data (skip first sample)
          if (prevSnap && packetsDelta >= 0) {
            tracks.push(info);
          }
        }
      }
    } catch {
      // Stats not available
    }
  }

  // Collect remote (recv) track stats
  for (const participant of room.remoteParticipants.values()) {
    for (const pub of participant.trackPublications.values()) {
      const rp = pub as RemoteTrackPublication;
      if (!rp.track?.receiver) continue;

      try {
        const report = await rp.track.receiver.getStats();

        const codecs = new Map<string, string>();
        for (const stat of report.values()) {
          if (stat.type === "codec" && stat.mimeType) {
            codecs.set(stat.id, stat.mimeType.split("/")[1]?.toUpperCase() ?? "");
          }
        }

        for (const stat of report.values()) {
          if (stat.type === "inbound-rtp" && (stat.kind === "video" || stat.kind === "audio")) {
            const prevSnap = prevReport.get(stat.id);
            const dt = prevSnap ? (now - prevSnap.timestamp) / 1000 : 1;
            const bytesDelta = prevSnap ? stat.bytesReceived - prevSnap.bytes : 0;
            const bitrate = dt > 0 ? (bytesDelta * 8) / dt : 0;

            const packetsDelta = prevSnap ? stat.packetsReceived - prevSnap.packets : 0;
            const lostDelta = prevSnap ? stat.packetsLost - prevSnap.packetsLost : 0;
            const totalPkts = packetsDelta + lostDelta;
            const packetLoss = totalPkts > 0 ? (lostDelta / totalPkts) * 100 : 0;

            const info: TrackStats = {
              kind: stat.kind,
              direction: "recv",
              label: stat.kind === "video" ? "Video" : "Audio",
              bitrate,
              packetLoss: Math.max(0, packetLoss),
              jitter: stat.jitter ? stat.jitter * 1000 : undefined,
              active: prevSnap ? bytesDelta > 0 : true,
            };

            if (stat.kind === "video") {
              info.resolution = stat.frameWidth && stat.frameHeight
                ? `${stat.frameWidth}x${stat.frameHeight}`
                : undefined;
              info.framerate = stat.framesPerSecond ?? undefined;
            }

            if (stat.codecId) {
              info.codec = codecs.get(stat.codecId);
            }

            prevReport.set(stat.id, {
              bytes: stat.bytesReceived,
              packets: stat.packetsReceived,
              packetsLost: stat.packetsLost,
              timestamp: now,
            });

            if (prevSnap) {
              tracks.push(info);
            }
          }
        }
      } catch {
        // Stats not available
      }
    }
  }

  // Get RTT from candidate-pair
  let rtt: number | undefined;
  try {
    const pc = (room.engine as unknown as { publisher?: { pc?: RTCPeerConnection } })?.publisher?.pc;
    if (pc) {
      const report = await pc.getStats();
      for (const stat of report.values()) {
        if (stat.type === "candidate-pair" && stat.state === "succeeded" && stat.currentRoundTripTime) {
          rtt = stat.currentRoundTripTime * 1000;
          break;
        }
      }
    }
  } catch {
    // RTT not available
  }

  const totalSendBitrate = tracks
    .filter((t) => t.direction === "send")
    .reduce((sum, t) => sum + t.bitrate, 0);
  const totalRecvBitrate = tracks
    .filter((t) => t.direction === "recv")
    .reduce((sum, t) => sum + t.bitrate, 0);

  // Get actual camera capture resolution from the MediaStreamTrack
  let capture: CaptureInfo | undefined;
  for (const pub of room.localParticipant.trackPublications.values()) {
    const lp = pub as LocalTrackPublication;
    if (lp.source === Track.Source.Camera && lp.track?.mediaStreamTrack) {
      const settings = lp.track.mediaStreamTrack.getSettings();
      if (settings.width && settings.height) {
        capture = { resolution: `${settings.width}x${settings.height}` };
      }
      const constraints = lp.track.mediaStreamTrack.getConstraints();
      const cw = constraints.width;
      const ch = constraints.height;
      if (cw && ch) {
        const rw = typeof cw === "object" ? (cw.ideal ?? cw.max ?? cw.min) : cw;
        const rh = typeof ch === "object" ? (ch.ideal ?? ch.max ?? ch.min) : ch;
        if (rw && rh) {
          capture = { ...capture, requested: `${rw}x${rh}` };
        }
      }
      break;
    }
  }

  return { tracks, rtt, totalSendBitrate, totalRecvBitrate, capture, timestamp: now };
}

function StatRow({ label, value, warn, dim }: { label: string; value: string; warn?: boolean; dim?: boolean }) {
  return (
    <div className="flex justify-between items-center py-0.5">
      <span className={`text-xs ${dim ? "text-muted-foreground/60" : "text-muted-foreground"}`}>{label}</span>
      <span className={`text-xs font-mono ${warn ? "text-amber-400" : dim ? "text-muted-foreground/60" : "text-foreground"}`}>
        {value}
      </span>
    </div>
  );
}

function TrackStatsCard({ stat }: { stat: TrackStats }) {
  const Icon = stat.direction === "send" ? MonitorUp : MonitorDown;
  const dirLabel = stat.direction === "send" ? "TX" : "RX";
  const layerLabel = stat.rid ? ridLabels[stat.rid] ?? stat.rid : null;
  const dim = !stat.active;

  return (
    <div className={`border rounded-md p-2 space-y-0.5 ${dim ? "border-border/30 opacity-50" : "border-border/50"}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3 h-3 text-muted-foreground" />
        <span className="text-xs font-medium text-foreground">
          {dirLabel} {stat.label}
        </span>
        {layerLabel && (
          <span className={`text-[10px] px-1 rounded ${
            stat.rid === "f" ? "bg-green-500/20 text-green-400" :
            stat.rid === "h" ? "bg-blue-500/20 text-blue-400" :
            "bg-muted text-muted-foreground"
          }`}>
            {layerLabel}
          </span>
        )}
        {stat.codec && (
          <span className="text-[10px] bg-muted px-1 rounded text-muted-foreground">{stat.codec}</span>
        )}
        {!stat.active && (
          <span className="text-[10px] bg-muted px-1 rounded text-muted-foreground italic">paused</span>
        )}
      </div>
      <StatRow label="Bitrate" value={formatBitrate(stat.bitrate)} dim={dim} />
      {stat.targetBitrate !== undefined && stat.targetBitrate > 0 && (
        <StatRow label="Target" value={formatBitrate(stat.targetBitrate)} dim={dim} />
      )}
      {stat.resolution && <StatRow label="Resolution" value={stat.resolution} dim={dim} />}
      {stat.framerate !== undefined && <StatRow label="FPS" value={`${stat.framerate.toFixed(0)}`} dim={dim} />}
      <StatRow
        label="Packet Loss"
        value={`${stat.packetLoss.toFixed(1)}%`}
        warn={stat.packetLoss > 2}
        dim={dim}
      />
      {stat.jitter !== undefined && (
        <StatRow label="Jitter" value={`${stat.jitter.toFixed(1)} ms`} warn={stat.jitter > 30} dim={dim} />
      )}
      {stat.qualityLimitationReason && stat.qualityLimitationReason !== "none" && (
        <StatRow
          label="Limited by"
          value={limitationLabels[stat.qualityLimitationReason] ?? stat.qualityLimitationReason}
          warn
        />
      )}
    </div>
  );
}

/** Configured publish settings from useVideoRoom for reference */
const CONFIGURED = {
  requestedCapture: "1920x1080",
  maxVideoBitrate: "6 Mbps",
  maxScreenBitrate: "8 Mbps",
  videoCodec: "VP9",
  simulcast: "On",
};

export function VideoDebugStats({ room }: { room: Room | null }) {
  const [stats, setStats] = useState<RoomStats | null>(null);

  const poll = useCallback(async () => {
    if (!room || room.state !== "connected") {
      setStats(null);
      return;
    }
    const s = await collectStats(room);
    setStats(s);
  }, [room]);

  useEffect(() => {
    prevReport = new Map();
    poll();
    const id = setInterval(poll, 2000);
    return () => clearInterval(id);
  }, [poll]);

  if (!room || !stats || stats.tracks.length === 0) {
    return (
      <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="w-4 h-4 text-amber-500" />
          <h4 className="font-medium text-amber-500 text-sm">Debug: Video Stats</h4>
        </div>
        <p className="text-xs text-muted-foreground">
          {!room ? "No room connected" : "Collecting stats..."}
        </p>
      </div>
    );
  }

  const sendTracks = stats.tracks.filter((t) => t.direction === "send");
  const recvTracks = stats.tracks.filter((t) => t.direction === "recv");

  // Group send video tracks: show simulcast layers together
  const sendVideo = sendTracks.filter((t) => t.kind === "video");
  const sendAudio = sendTracks.filter((t) => t.kind === "audio");
  const hasSimulcast = sendVideo.some((t) => t.rid);

  return (
    <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-4 h-4 text-amber-500" />
        <h4 className="font-medium text-amber-500 text-sm">Debug: Video Stats</h4>
      </div>

      {/* Summary row */}
      <div className="flex gap-3 mb-2 text-xs">
        {stats.rtt !== undefined && (
          <span className={stats.rtt > 150 ? "text-amber-400" : "text-muted-foreground"}>
            RTT {stats.rtt.toFixed(0)}ms
          </span>
        )}
        <span className="text-muted-foreground">
          TX {formatBitrate(stats.totalSendBitrate)}
        </span>
        <span className="text-muted-foreground">
          RX {formatBitrate(stats.totalRecvBitrate)}
        </span>
      </div>

      {/* Camera capture info */}
      {stats.capture && (
        <div className="mb-2 text-xs space-y-0.5">
          <StatRow
            label="Camera capture"
            value={stats.capture.resolution ?? "N/A"}
            warn={stats.capture.resolution !== CONFIGURED.requestedCapture}
          />
          {stats.capture.resolution !== CONFIGURED.requestedCapture && (
            <p className="text-[10px] text-amber-400">
              Requested {CONFIGURED.requestedCapture} — webcam hardware limit
            </p>
          )}
        </div>
      )}

      {/* Adaptive stream / quality preference */}
      <StatRow
        label="Adaptive stream"
        value={room.options.adaptiveStream ? "On (auto)" : "Off (prefer high)"}
        dim
      />

      {/* Configured settings */}
      <details className="mb-2">
        <summary className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold cursor-pointer flex items-center gap-1">
          <Settings2 className="w-3 h-3" />
          Configured
        </summary>
        <div className="mt-1 pl-4 border-l border-border/30">
          <StatRow label="Requested" value={CONFIGURED.requestedCapture} dim />
          <StatRow label="Max Video" value={CONFIGURED.maxVideoBitrate} dim />
          <StatRow label="Max Screen" value={CONFIGURED.maxScreenBitrate} dim />
          <StatRow label="Codec" value={CONFIGURED.videoCodec} dim />
          <StatRow label="Simulcast" value={CONFIGURED.simulcast} dim />
        </div>
      </details>

      {/* Sending */}
      {sendTracks.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            Sending
            {hasSimulcast && (
              <span className="normal-case font-normal ml-1">(simulcast: {sendVideo.filter(t => t.active).length}/{sendVideo.length} layers active)</span>
            )}
          </p>
          {sendAudio.map((t, i) => (
            <TrackStatsCard key={`send-a-${i}`} stat={t} />
          ))}
          {sendVideo.map((t, i) => (
            <TrackStatsCard key={`send-v-${i}`} stat={t} />
          ))}
        </div>
      )}

      {/* Receiving */}
      {recvTracks.length > 0 && (
        <div className="mt-2 space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Receiving</p>
          {recvTracks.map((t, i) => (
            <TrackStatsCard key={`recv-${i}`} stat={t} />
          ))}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground mt-2 text-right">
        Updated {new Date(stats.timestamp).toLocaleTimeString()}
      </p>
    </div>
  );
}
